import {Component, Input, ChangeDetectionStrategy} from '@angular/core';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faCheck, faCircleExclamation, faCircleXmark,} from '@fortawesome/pro-solid-svg-icons';

export interface LogEntry {
  timestamp?: string;
  level: 'INFO' | 'WARNING' | 'ERROR' | 'DEBUG';
  title?: string;
  message?: string;
}

type LogFilter = 'ALL' | 'INFO' | 'WARNING' | 'ERROR';

@Component({
  selector: 'app-workflow-log-panel',
  standalone: true,
  imports: [FaIconComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="flex flex-col border border-gray-200 rounded-lg bg-white shadow-lg">
      <!-- Header -->
      <div class="flex items-center justify-between px-4 py-3">
        <span class="text-sm font-medium text-gray-900">Logs</span>
        <div class="flex items-center gap-4">
          <!-- Filter badges -->
          <button (click)="setFilter('ALL')"
                  class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-medium transition-colors"
                  [class.bg-green-50]="filter === 'ALL'"
                  [class.text-green-600]="filter === 'ALL'"
                  [class.bg-gray-100]="filter !== 'ALL'"
                  [class.text-gray-600]="filter !== 'ALL'">
            <fa-icon [icon]="faCheck" class="text-[10px]"></fa-icon>
            Erfolg
          </button>
          <button (click)="setFilter('WARNING')"
                  class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-medium transition-colors"
                  [class.bg-yellow-100]="filter === 'WARNING'"
                  [class.text-yellow-700]="filter === 'WARNING'"
                  [class.bg-gray-100]="filter !== 'WARNING'"
                  [class.text-gray-600]="filter !== 'WARNING'">
            <fa-icon [icon]="faCircleExclamation" class="text-[10px]"></fa-icon>
            Warnung
          </button>
          <button (click)="setFilter('ERROR')"
                  class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-medium transition-colors"
                  [class.bg-red-50]="filter === 'ERROR'"
                  [class.text-red-600]="filter === 'ERROR'"
                  [class.bg-gray-100]="filter !== 'ERROR'"
                  [class.text-gray-600]="filter !== 'ERROR'">
            <fa-icon [icon]="faCircleXmark" class="text-[10px]"></fa-icon>
            Fehler
          </button>
        </div>
      </div>

      <!-- Log entries -->
      <div class="max-h-[200px] overflow-y-auto px-4 pb-3 flex flex-col gap-3">
        @for (log of filteredLogs; track $index) {
          <div class="flex items-center gap-6">
            <!-- Timestamp -->
            <span class="text-xs text-gray-900 w-[60px] shrink-0">{{ formatTimestamp(log.timestamp) }}</span>

            <!-- Level badge -->
            <div class="w-[60px] shrink-0">
              <span class="inline-flex items-center justify-center w-full px-2.5 py-0.5 rounded-md text-xs font-medium"
                    [class.bg-gray-200]="log.level === 'INFO' || log.level === 'DEBUG'"
                    [class.text-gray-700]="log.level === 'INFO' || log.level === 'DEBUG'"
                    [class.bg-yellow-100]="log.level === 'WARNING'"
                    [class.text-yellow-700]="log.level === 'WARNING'"
                    [class.bg-red-50]="log.level === 'ERROR'"
                    [class.text-red-600]="log.level === 'ERROR'">
                {{ getLevelLabel(log.level) }}
              </span>
            </div>

            <!-- Title -->
            <span class="text-sm font-medium text-gray-900 w-[160px] shrink-0 truncate"
                  [class.text-red-600]="log.level === 'ERROR'">
              {{ log.title }}
            </span>

            <!-- Message -->
            <span class="text-sm text-gray-900 truncate flex-1"
                  [class.text-red-600]="log.level === 'ERROR'">
              {{ log.message }}
            </span>
          </div>
        }
        @if (filteredLogs.length === 0) {
          <span class="text-sm text-gray-400 text-center py-4">Keine Log-Einträge vorhanden</span>
        }
      </div>
    </div>
  `,
})
export class WorkflowLogPanelComponent {
  filter: LogFilter = 'ALL';
  protected readonly faCheck = faCheck;
  protected readonly faCircleExclamation = faCircleExclamation;
  protected readonly faCircleXmark = faCircleXmark;

  _logs: LogEntry[] = [];

  @Input() set logs(value: any[] | undefined | null) {
    this._logs = (value ?? []).map(l => ({
      timestamp: l.timestamp ?? l.time ?? null,
      level: (l.level ?? 'INFO').toUpperCase(),
      title: l.title ?? l.module ?? '',
      message: l.message ?? l.msg ?? '',
    }));
  }

  get filteredLogs(): LogEntry[] {
    if (this.filter === 'ALL') return this._logs;
    return this._logs.filter(l => l.level === this.filter);
  }

  setFilter(f: LogFilter): void {
    this.filter = this.filter === f ? 'ALL' : f;
  }

  formatTimestamp(ts?: string): string {
    if (!ts) return '--:--:--';
    try {
      const d = new Date(ts);
      return d.toLocaleTimeString('de-DE', {hour: '2-digit', minute: '2-digit', second: '2-digit'});
    } catch {
      return ts;
    }
  }

  getLevelLabel(level: string): string {
    switch (level) {
      case 'ERROR':
        return 'Fehler';
      case 'WARNING':
        return 'Warnung';
      case 'DEBUG':
        return 'Debug';
      default:
        return 'Info';
    }
  }
}


